import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { api } from '@/lib/api';

const CATEGORIES = ['Conduct', 'Safety', 'Workplace', 'Payroll', 'Harassment', 'Performance', 'Other'];
const SEVERITIES = ['Complaint', 'Misconduct', 'Gross misconduct'];

export default function NewCaseForm({ user, onClose, onCreated }) {
  const [crew, setCrew] = useState([]);
  const [vessels, setVessels] = useState([]);
  const [form, setForm] = useState({
    case_type: 'Disciplinary',
    subject: '',
    crew_id: '',
    vessel: user.role === 'vessel_hr' ? (user.vessel || '') : '',
    category: 'Conduct',
    severity: 'Misconduct',
    summary: '',
    next_action: 'Review case details',
    follow_up: '',
  });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([api('/crew'), api('/vessels')]).then(([rows, vs]) => {
      setCrew(rows);
      const names = vs.map((v) => v.name);
      setVessels(names);
      if (!form.vessel && names.length) setForm((f) => ({ ...f, vessel: names[0] }));
    });
    /* eslint-disable-next-line */
  }, []);

  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const payload = { ...form };
      if (!payload.follow_up) delete payload.follow_up;
      await api('/cases', { method: 'POST', body: JSON.stringify(payload) });
      onCreated?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="drawer-overlay" onClick={onClose}>
      <form className="drawer" onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="new-case-drawer">
        <div className="drawer-head">
          <div><span className="eyebrow">NEW CASE</span><h2>Open a new case</h2></div>
          <button type="button" className="icon-btn" data-testid="new-case-close" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="drawer-body stack">
          <div className="row-two">
            <label className="field"><span>Case type</span>
              <select data-testid="new-case-type" value={form.case_type} onChange={(e) => set('case_type', e.target.value)}>
                <option>Disciplinary</option><option>Grievance</option>
              </select>
            </label>
            <label className="field"><span>Severity</span>
              <select data-testid="new-case-severity" value={form.severity} onChange={(e) => set('severity', e.target.value)}>
                {SEVERITIES.map((s) => <option key={s}>{s}</option>)}
              </select>
            </label>
          </div>
          <label className="field"><span>Subject (crew member name)</span>
            <input required data-testid="new-case-subject" value={form.subject} onChange={(e) => set('subject', e.target.value)} placeholder="e.g. Amelia Tan" />
          </label>
          <div className="row-two">
            <label className="field"><span>Linked crew record (optional)</span>
              <select data-testid="new-case-crew" value={form.crew_id} onChange={(e) => set('crew_id', e.target.value)}>
                <option value="">— None —</option>
                {crew.map((c) => <option key={c.id} value={c.id}>{c.name} · {c.rank}</option>)}
              </select>
            </label>
            <label className="field"><span>Vessel</span>
              <select required data-testid="new-case-vessel" value={form.vessel} onChange={(e) => set('vessel', e.target.value)} disabled={user.role === 'vessel_hr'}>
                {vessels.map((v) => <option key={v}>{v}</option>)}
              </select>
            </label>
          </div>
          <div className="row-two">
            <label className="field"><span>Category</span>
              <select data-testid="new-case-category" value={form.category} onChange={(e) => set('category', e.target.value)}>
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </label>
            <label className="field"><span>Follow-up date</span>
              <input type="date" data-testid="new-case-followup" value={form.follow_up} onChange={(e) => set('follow_up', e.target.value)} />
            </label>
          </div>
          <label className="field"><span>Summary</span>
            <textarea required data-testid="new-case-summary" value={form.summary} onChange={(e) => set('summary', e.target.value)} placeholder="Brief case summary" />
          </label>
          <label className="field"><span>Next action</span>
            <input data-testid="new-case-next-action" value={form.next_action} onChange={(e) => set('next_action', e.target.value)} />
          </label>
          {error && <div data-testid="new-case-error" className="error">{error}</div>}
          <button className="primary-btn" data-testid="new-case-submit" type="submit" disabled={busy}>
            {busy ? 'Creating…' : 'Create case'}
          </button>
        </div>
      </form>
    </div>
  );
}
